import { useEffect, useState } from 'react';
import { BookmarkPlus, Repeat } from 'lucide-react';
import type { KeepaFilters, KeepaQuerySchedule, KeepaQueryTemplate } from '@sourcito/schemas';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  Input,
} from '../design';
import { ApiCallError } from '../lib/api';
import { useCreateKeepaTemplate, useCreateKeepaTemplateWithSchedule } from '../lib/keepaTemplates';

interface SaveQueryDialogProps {
  /** The translated KeepaFilters blob the operator just ran and wants to freeze. */
  filters: KeepaFilters;
  /** Initial NL text (truncated server-side) used to pre-fill the name. */
  initialName?: string;
  /**
   * The raw natural-language query the operator typed before the LLM translated
   * it. Persisted on the template so /templates and the SearchPage can show
   * "Original query" — i.e., why this template exists. Pass null/undefined for
   * code paths that don't have an NL source (e.g., saving a built-in clone).
   */
  naturalLanguageQuery?: string | null;
  /**
   * When true, the dialog renders as "Save & start hourly crawl" — saves the
   * template AND creates an enabled hourly schedule against it in one round
   * trip. The schedule defaults (cron, monthlyRunCap) are shown but not
   * editable in this modal; the user can edit later via /schedules.
   * See docs/plans/2026-05-23-searchpage-save-schedule-ux-design.md §3 Path B.
   */
  withSchedule?: boolean;
  /** Fires after a successful save with the new template row. */
  onSaved: (template: KeepaQueryTemplate) => void;
  /**
   * Fires after a successful Save & start hourly with the new schedule row.
   * Only invoked when `withSchedule` is true.
   */
  onScheduled?: (schedule: KeepaQuerySchedule) => void;
  /** Disables the trigger when there's nothing to save. */
  disabled?: boolean;
  /** Trigger button label override. Default: "Save query". */
  triggerLabel?: string;
  /**
   * Tooltip rendered on the trigger when `disabled` is true — lets callers
   * explain WHY (e.g., the hourly cap is already reached).
   */
  disabledReason?: string;
}

/**
 * Save-as-template dialog. Operates in two modes:
 *   - Default ("Save query"): persists a template only. No schedule.
 *   - withSchedule ("Save & start hourly"): persists template + hourly
 *     schedule via the combined /with-schedule endpoint.
 *
 * The combined path enforces the 1-enabled-hourly-schedule-per-tenant cap
 * at the API layer (409 HOURLY_LIMIT_REACHED). Callers pass `disabled` +
 * `disabledReason` to surface that as a tooltip on the trigger.
 *
 * See docs/plans/2026-05-23-searchpage-save-schedule-ux-design.md §3.
 */
export function SaveQueryDialog({
  filters,
  initialName,
  naturalLanguageQuery,
  withSchedule = false,
  onSaved,
  onScheduled,
  disabled,
  triggerLabel,
  disabledReason,
}: SaveQueryDialogProps) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [clientError, setClientError] = useState<string | null>(null);
  const create = useCreateKeepaTemplate();
  const createWithSchedule = useCreateKeepaTemplateWithSchedule();

  // Reset the form whenever the modal opens, pre-filling the name from the
  // operator's last NL query (truncated for the 120-char limit).
  useEffect(() => {
    if (!open) return;
    setName(deriveDefaultName(initialName));
    setDescription('');
    setClientError(null);
  }, [open, initialName]);

  async function handleSave() {
    const trimmedName = name.trim();
    if (trimmedName.length === 0) {
      setClientError('Name is required.');
      return;
    }
    if (trimmedName.length > 120) {
      setClientError('Name must be 120 characters or fewer.');
      return;
    }
    setClientError(null);

    const nlTrimmed = naturalLanguageQuery?.trim() ?? '';
    const baseBody = {
      name: trimmedName,
      description: description.trim() ? description.trim() : null,
      // Send the original NL text so the template carries provenance. Send
      // null when the source was an empty/whitespace string (e.g., the user
      // saved from a state with no typed NL — happens for clones of fixed
      // filters). Server zod schema accepts null/omitted.
      naturalLanguageQuery: nlTrimmed.length > 0 ? nlTrimmed : null,
      fixedFilters: filters as unknown as Record<string, unknown>,
      parameters: [],
    };

    try {
      if (withSchedule) {
        const result = await createWithSchedule.mutateAsync({
          ...baseBody,
          schedule: {
            // Default schedule name derives from the template name so /schedules
            // shows something readable. User can rename later from there.
            name: `${trimmedName} — hourly`,
            cronExpression: '0 * * * *',
            monthlyRunCap: 1000,
            parameterValues: {},
          },
        });
        setOpen(false);
        onSaved(result.template);
        onScheduled?.(result.schedule);
      } else {
        const saved = await create.mutateAsync(baseBody);
        setOpen(false);
        onSaved(saved);
      }
    } catch (err) {
      if (err instanceof ApiCallError) {
        setClientError(err.message);
      } else {
        setClientError((err as Error).message ?? 'Save failed.');
      }
    }
  }

  const isPending = withSchedule ? createWithSchedule.isPending : create.isPending;
  const defaultTriggerLabel = withSchedule ? 'Save & start hourly' : 'Save query';
  const dialogTitle = withSchedule ? 'Save & start hourly crawl' : 'Save this query';
  const submitLabel = withSchedule ? 'Save & start crawl' : 'Save';

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button
        type="button"
        variant="secondary"
        size="sm"
        leadingIcon={
          withSchedule ? <Repeat className="size-4" /> : <BookmarkPlus className="size-4" />
        }
        onClick={() => setOpen(true)}
        disabled={disabled}
        // disabledReason is only meaningful when disabled is true; otherwise
        // fall back to a short verb-tooltip explaining what each path does.
        title={
          disabled && disabledReason
            ? disabledReason
            : withSchedule
              ? 'Save these filters and start firing them hourly. Free Keepa cost to set up — the first run lands at the top of the next hour.'
              : 'Save these filters as a re-runnable template'
        }
      >
        {triggerLabel ?? defaultTriggerLabel}
      </Button>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
          <DialogDescription>
            {withSchedule ? (
              <>
                Saves the current Keepa filters as a re-runnable template AND starts a recurring
                crawl against it. The first fire lands at the top of the next hour.
              </>
            ) : (
              <>
                Persists the current Keepa filters as a re-runnable template. No more LLM cost on
                subsequent runs — the filters are frozen and replay byte-for-byte. You can schedule
                it or run it again right from this page.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <FormField label="Name" htmlFor="save-query-name" required>
            <Input
              id="save-query-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Indie kitchen under $25"
              maxLength={120}
              autoFocus
            />
          </FormField>

          <FormField label="Description (optional)" htmlFor="save-query-description">
            <Input
              id="save-query-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What this query is for, when to run it, who cares about results"
              maxLength={500}
            />
          </FormField>

          {withSchedule && (
            // Schedule defaults — surfaced so the user knows what's about to
            // happen. Intentionally read-only here per §3 of the design doc;
            // editing happens in the Active Crawl panel / /schedules.
            <div className="rounded-sm border border-border-subtle bg-surface px-3 py-2.5 text-xs space-y-1 text-fg-secondary">
              <p>
                <span className="text-fg-tertiary">▶ Schedule:</span> hourly{' '}
                <span className="font-mono text-fg-tertiary">(0 * * * *)</span>
              </p>
              <p>
                <span className="text-fg-tertiary">▶ First fire:</span> at the top of the next hour
              </p>
              <p>
                <span className="text-fg-tertiary">▶ Monthly cap:</span> 1000 runs (~720/mo
                expected)
              </p>
              <p className="text-fg-tertiary pt-0.5 italic">
                These are defaults — edit later in the Active Crawl panel.
              </p>
            </div>
          )}

          {clientError && <p className="text-sm text-negative">{clientError}</p>}
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost" size="sm">
              Cancel
            </Button>
          </DialogClose>
          <Button onClick={handleSave} loading={isPending} size="sm">
            {submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function deriveDefaultName(initial: string | undefined): string {
  if (!initial) return '';
  const trimmed = initial.trim();
  if (trimmed.length <= 60) return trimmed;
  return `${trimmed.slice(0, 57).trimEnd()}…`;
}
